<script setup>
import { computed, ref, watch } from 'vue'
import { Badge, Button, EmptyState, Input, Pagination, Select, Spinner } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { listIntentVideoTaskSources } from '../../api/intent-videos.js'
import { findLlmTurnConversations } from '../../api/llm/index.js'
import { useAdminAuth } from '../../composables/useAdminAuth.js'
import LongText from '../../components/LongText.vue'

const props = defineProps({ requestId: { type: String, required: true } })

const { t } = useI18n()
const router = useRouter()
const auth = useAdminAuth()
const loading = ref(false)
const error = ref('')
const items = ref([])
const total = ref(0)
const pages = ref(0)
const page = ref(1)
const limit = ref(20)
const q = ref('')
const order = ref('desc')
const conversationsByTurn = ref(new Map())
const canReadConversations = computed(() => auth.account.value.role === 'admin')
const orderOptions = computed(() => [
  { value: 'desc', label: t('visualGameAdmin.intentVideo.taskSources.newest') },
  { value: 'asc', label: t('visualGameAdmin.intentVideo.taskSources.oldest') },
])

watch(() => props.requestId, async () => {
  page.value = 1
  await load()
}, { immediate: true })

async function load() {
  loading.value = true
  error.value = ''
  try {
    const response = await listIntentVideoTaskSources({
      request_id: props.requestId,
      page: page.value,
      limit: limit.value,
      q: q.value.trim(),
      order: order.value,
    })
    items.value = response.items
    total.value = response.total
    pages.value = response.pages
    conversationsByTurn.value = canReadConversations.value
      ? await loadConversations(response.items)
      : new Map()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

async function loadConversations(sourceItems) {
  if (sourceItems.length === 0) return new Map()
  const response = await findLlmTurnConversations(sourceItems.map(item => item.turn_id))
  return new Map(response.items.map(item => [item.turn_id, item.conversations]))
}

function applySearch() {
  page.value = 1
  return load()
}

function previousPage() {
  page.value -= 1
  return load()
}

function nextPage() {
  page.value += 1
  return load()
}

function openConversation(source, conversation) {
  router.push({
    path: '/llm/conversations',
    query: {
      run_id: conversation.run_id,
      conversation_key: conversation.conversation_key,
      turn_id: source.turn_id,
    },
  })
}
</script>

<template>
  <section class="task-sources">
    <h3>{{ t('visualGameAdmin.intentVideo.taskSources.title', { n: total }) }}</h3>
    <div class="task-sources__filters">
      <Input
        v-model="q"
        type="search"
        size="sm"
        :placeholder="t('visualGameAdmin.intentVideo.taskSources.search')"
        @keyup.enter="applySearch"
      />
      <Select v-model="order" :options="orderOptions" size="sm" @update:model-value="applySearch" />
      <Button size="sm" :loading="loading" @click="applySearch">
        {{ t('visualGameAdmin.intentVideo.taskSources.apply') }}
      </Button>
    </div>

    <div v-if="loading" class="task-sources__state"><Spinner /></div>
    <p v-else-if="error" class="vga-error">{{ error }}</p>
    <EmptyState
      v-else-if="items.length === 0"
      :title="t('visualGameAdmin.intentVideo.taskSources.empty')"
    />
    <ol v-else class="task-sources__list">
      <li v-for="source in items" :key="source.turn_id">
        <header>
          <span class="vga-mono">{{ source.turn_id }}</span>
          <Badge size="sm">{{ t('visualGameAdmin.intentVideo.taskSources.turn', { n: source.turn_no }) }}</Badge>
        </header>
        <LongText
          :label="t('visualGameAdmin.intentVideo.taskSources.playerInput')"
          :text="source.input_text"
          :copy-text="source.input_text"
          :collapse-over="300"
          markdown
        />
        <div v-if="canReadConversations" class="task-sources__conversations">
          <span v-if="conversationsByTurn.get(source.turn_id).length === 0">
            {{ t('visualGameAdmin.intentVideo.taskSources.noConversation') }}
          </span>
          <Button
            v-for="conversation in conversationsByTurn.get(source.turn_id)"
            :key="`${conversation.agent_role}:${conversation.conversation_key}`"
            size="sm"
            variant="ghost"
            @click="openConversation(source, conversation)"
          >
            {{ t('visualGameAdmin.intentVideo.taskSources.openConversation', { role: conversation.agent_role }) }}
          </Button>
        </div>
      </li>
    </ol>

    <Pagination
      v-if="pages > 1"
      :has-prev="page > 1"
      :has-next="page < pages"
      :loading="loading"
      :summary="t('visualGameAdmin.intentVideo.taskSources.page', { page, pages })"
      :prev-label="t('visualGameAdmin.intentVideo.pager.prev')"
      :next-label="t('visualGameAdmin.intentVideo.pager.next')"
      @prev="previousPage"
      @next="nextPage"
    />
  </section>
</template>

<style scoped>
.task-sources {
  min-width: 0;
}

.task-sources > h3 {
  margin: 0;
  font-size: var(--drama-font-size-md);
  font-weight: var(--drama-font-weight-semibold);
}

.task-sources__filters {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: var(--drama-space-sm);
  margin: var(--drama-space-md) 0;
}

.task-sources__state {
  display: grid;
  place-items: center;
  min-height: 96px;
}

.task-sources__list {
  display: grid;
  gap: var(--drama-space-sm);
  margin: 0;
  padding: 0;
  list-style: none;
}

.task-sources__list > li {
  display: grid;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-sm) var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
}

.task-sources__list header,
.task-sources__conversations {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
}

.task-sources__list header {
  justify-content: space-between;
}

.task-sources__list header span {
  overflow-wrap: anywhere;
}

.task-sources__list :deep(.long-text) {
  gap: var(--drama-space-xs);
}

.task-sources__list :deep(.long-text__body) {
  padding: var(--drama-space-sm);
}

@media (max-width: 680px) {
  .task-sources__filters {
    grid-template-columns: 1fr;
  }
}
</style>
